/**
 * A 1:1 mirror of the schema fields which we use in LSC:
 * https://locations.g-experts.net/interface/v2/locations_schema.json
 */
enum SchemaField {
  storeCode = 'storeCode',
  businessName = 'businessName',

  // web-related
  website = 'website',
  appointmentURL = 'appointmentURL',
  menuURL = 'menuURL',
  reservationsURL = 'reservationsURL',
  orderAheadURL = 'orderAheadURL',
  socialMediaUrls = 'socialMediaUrls',

  // address
  addressLine1 = 'addressLine1',
  addressLine2 = 'addressLine2',
  addressLine3 = 'addressLine3',
  addressLine4 = 'addressLine4',
  addressLine5 = 'addressLine5',
  state = 'state',
  district = 'district',
  city = 'city',
  country = 'country',
  postalCode = 'postalCode',

  // geo
  latitude = 'latitude',
  longitude = 'longitude',

  // biz hrs
  mondayHours = 'mondayHours',
  tuesdayHours = 'tuesdayHours',
  wednesdayHours = 'wednesdayHours',
  thursdayHours = 'thursdayHours',
  fridayHours = 'fridayHours',
  saturdayHours = 'saturdayHours',
  sundayHours = 'sundayHours',

  moreHours = 'moreHours',
  specialHours = 'specialHours',
  openingDate = 'openingDate',
  temporarilyClosed = 'temporarilyClosed',

  // phones
  primaryPhone = 'primaryPhone',
  additionalPhones = 'additionalPhones', // note the plural; throughout the app we're using 'additionalPhone'
  adwords = 'adwords', // for historical purposes renamed to 'adwordsLocation' throughout the app

  // categories
  primaryCategory = 'primaryCategory',
  additionalCategories = 'additionalCategories', // note the plural; throughout the app we're using 'additionalCategory'

  // description + labels
  fromTheBusiness = 'fromTheBusiness',
  labels = 'labels',
  customServices = 'customServices',

  // photos
  logoPhoto = 'logoPhoto',
  coverPhoto = 'coverPhoto',
  otherPhotos = 'otherPhotos'
}

/**
 * The `LSCField` overlaps with `SchemaField` in quite a few places but introduces
 * new field names that are used throughout LSC.
 *
 * It'd be nice if the overlaps were directly shared with the above enum
 * but that's not possible till https://github.com/microsoft/TypeScript/issues/40793
 * is resolved and https://github.com/microsoft/TypeScript/pull/50528 is merged.
 *
 * For now, we'll redeclare the fields again.
 */
export enum LSCField {
  id = 'id',
  placeId = 'placeId',
  locationId = 'locationId',
  gmbLocationId = 'gmbLocationId',
  gmbAccountIds = 'gmbAccountIds',

  storeCode = 'storeCode',
  businessName = 'businessName',
  existsInGMB = 'existsInGMB',

  status = 'status',
  businessStatus = 'businessStatus',
  managementStatus = 'managementStatus',

  comment = 'comment',
  differences = 'differences',
  acceptedDifferences = 'acceptedDifferences',
  differentInGMB = 'differentInGMB',

  blackListedFields = 'blackListedFields',
  dataGoldmine = 'dataGoldmine',
  verified = 'verified',

  editTimestamp = 'editTimestamp',
  claimAndCleanCompletionTimestamp = 'claimAndCleanCompletionTimestamp',

  // web-related
  website = 'website',
  appointmentURL = 'appointmentURL',
  menuURL = 'menuURL',
  reservationsURL = 'reservationsURL',
  orderAheadURL = 'orderAheadURL',
  socialMediaUrls = 'socialMediaUrls',

  myBusinessLocationUrl = 'myBusinessLocationUrl',
  searchUrl = 'searchUrl',
  mapsUrl = 'mapsUrl',

  // address
  addressLine1 = 'addressLine1',
  addressLine2 = 'addressLine2',
  addressLine3 = 'addressLine3',
  addressLine4 = 'addressLine4',
  addressLine5 = 'addressLine5',
  state = 'state',
  district = 'district',
  city = 'city',
  country = 'country',
  postalCode = 'postalCode',
  address = 'address', // combined address

  // geo
  latitude = 'latitude',
  longitude = 'longitude',
  coordinate = 'coordinate',
  coordinateImport = 'coordinateImport',

  // biz hrs
  mondayHours = 'mondayHours',
  tuesdayHours = 'tuesdayHours',
  wednesdayHours = 'wednesdayHours',
  thursdayHours = 'thursdayHours',
  fridayHours = 'fridayHours',
  saturdayHours = 'saturdayHours',
  sundayHours = 'sundayHours',

  businessHours = 'businessHours',
  gxSpecialHours = 'gxSpecialHours',

  regularHours = 'regularHours', // mo-su hours combined
  specialHours = 'specialHours',
  openingDate = 'openingDate',
  temporarilyClosed = 'temporarilyClosed',

  // phones
  primaryPhone = 'primaryPhone',
  additionalPhone = 'additionalPhone', // note the discrepancy towards `additionalPhones` (plural)
  phoneNumbers = 'phoneNumbers', // primary + additional numbers
  adwordsLocation = 'adwordsLocation', // note the discrepancy towards `adwords`

  // categories
  primaryCategory = 'primaryCategory',
  additionalCategory = 'additionalCategory', // note the discrepancy towards `additionalCategories` (plural)
  categories = 'categories', // = primary + secondary categs combined

  // description + labels
  fromTheBusiness = 'fromTheBusiness',
  labels = 'labels',
  customServices = 'customServices',

  // photos
  logoPhoto = 'logoPhoto',
  coverPhoto = 'coverPhoto',
  otherPhotos = 'otherPhotos',

  // errors
  lastImportErrorMessage = 'lastImportErrorMessage',
  lastUpdateErrorMessage = 'lastUpdateErrorMessage'
}
